import { writeBatch, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { UserProfile, Quest, DailyActivity } from '../types';
import { rateLimitedFirestoreService } from './rateLimitedFirestoreService';

// Helper function to recursively remove undefined values from objects
const cleanObject = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => cleanObject(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const [key, value] of Object.entries(obj)) {
      if (value !== undefined) {
        cleaned[key] = cleanObject(value);
      }
    }
    return cleaned;
  }
  
  return obj;
};

/**
 * Optimized Firestore Sync Service with Rate Limiting
 * - Batches multiple operations to reduce writes
 * - Debounces frequent updates
 * - Applies rate limiting to prevent abuse
 * - Handles errors gracefully
 * 
 * IMPORTANT: Now uses rateLimitedFirestoreService internally to prevent bot abuse
 */

class FirestoreSyncService {
  private syncQueue: Map<string, any> = new Map();
  private syncTimer: NodeJS.Timeout | null = null;
  private readonly DEBOUNCE_DELAY = 1000; // 1 second debounce
  private isSyncing = false;

  /**
   * Queue an update to be synced (debounced)
   */
  queueSync(key: string, data: any) {
    this.syncQueue.set(key, data);
    this.scheduleBatchSync();
  }

  /**
   * Schedule a batch sync with debouncing
   */
  private scheduleBatchSync() {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = setTimeout(() => {
      this.executeBatchSync();
    }, this.DEBOUNCE_DELAY);
  }

  /**
   * Force immediate sync (bypasses debounce)
   */
  async forceSync() {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    await this.executeBatchSync();
  }

  /**
   * Execute all queued syncs with rate limiting
   * Groups operations by type and uses rateLimitedFirestoreService
   */
  private async executeBatchSync() {
    if (this.isSyncing || this.syncQueue.size === 0) {
      return;
    }

    this.isSyncing = true;
    const itemsToSync = new Map(this.syncQueue);
    this.syncQueue.clear();

    try {
      // Group operations by type for efficient processing
      const profiles: Array<{ userId: string; data: UserProfile }> = [];
      const quests: Array<{ userId: string; data: Quest }> = [];
      const statistics: Array<{ userId: string; data: DailyActivity }> = [];

      // Categorize items
      itemsToSync.forEach((data, key) => {
        const [collection, docId] = key.split(':');
        
        if (collection === 'users') {
          profiles.push({ userId: docId, data });
        } else if (collection === 'quests') {
          quests.push({ userId: data.userId, data });
        } else if (collection === 'statistics') {
          statistics.push({ userId: data.userId, data });
        }
      });

      // Process each type through rate-limited service
      const promises: Promise<any>[] = [];

      // Sync profiles
      for (const { userId, data } of profiles) {
        promises.push(rateLimitedFirestoreService.saveUserProfile(userId, data));
      }

      // Sync quests
      if (quests.length > 0) {
        // Group quests by userId for efficiency
        const questsByUser = new Map<string, Quest[]>();
        quests.forEach(({ userId, data }) => {
          if (!questsByUser.has(userId)) {
            questsByUser.set(userId, []);
          }
          questsByUser.get(userId)!.push(data);
        });

        // Save each user's quests in a batch
        for (const [userId, userQuests] of questsByUser) {
          promises.push(rateLimitedFirestoreService.saveQuests(userId, userQuests));
        }
      }

      // Sync statistics
      for (const { userId, data } of statistics) {
        promises.push(rateLimitedFirestoreService.saveDailyStatistic(userId, data));
      }

      // Execute all operations
      await Promise.all(promises);
    } catch (error) {
      console.error('❌ Error syncing to Firestore:', error);
      // Re-queue failed items
      itemsToSync.forEach((data, key) => {
        this.syncQueue.set(key, data);
      });
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Sync user profile (debounced)
   * 
   * 🔒 SECURITY: Exclude subscription, aiUsage, and lemonSqueezy fields from client-side syncing
   * These fields can only be modified by Cloud Functions, Stripe Extension, or Lemon Squeezy webhooks
   */
  syncProfile(userId: string, profile: UserProfile) {
    // Clean undefined fields to prevent Firestore errors
    const cleanProfile = cleanObject(profile);
    
    // 🔒 Remove protected fields (server-side only)
    const { subscription, aiUsage, lemonSqueezy, ...profileWithoutProtectedFields } = cleanProfile;
    
    this.queueSync(`users:${userId}`, profileWithoutProtectedFields);
  }

  /**
   * Sync single quest (debounced)
   */
  syncQuest(userId: string, quest: Quest) {
    // Clean undefined fields
    const cleanQuest = Object.fromEntries(
      Object.entries({ ...quest, userId }).filter(([_, v]) => v !== undefined)
    );
    this.queueSync(`quests:${quest.id}`, cleanQuest);
  }

  /**
   * Sync multiple quests efficiently
   */
  syncQuests(userId: string, quests: Quest[]) {
    quests.forEach(quest => {
      // Clean undefined fields
      const cleanQuest = Object.fromEntries(
        Object.entries({ ...quest, userId }).filter(([_, v]) => v !== undefined)
      );
      this.queueSync(`quests:${quest.id}`, cleanQuest);
    });
  }

  /**
   * Sync daily statistic (debounced)
   */
  syncStatistic(userId: string, statistic: DailyActivity) {
    this.queueSync(`statistics:${userId}_${statistic.date}`, { ...statistic, userId });
  }

  /**
   * Delete quest from Firestore (immediate, rate-limited)
   */
  async deleteQuest(questId: string): Promise<void> {
    try {
      await rateLimitedFirestoreService.deleteQuest(questId);
    } catch (error) {
      console.error('❌ Error deleting quest from Firestore:', error);
      throw error;
    }
  }

  /**
   * Batch sync profile + quests + stats (immediate, rate-limited)
   * Use this for critical operations like quest completion
   * @param bypassRateLimit - Set to true for trusted operations (e.g., AI program generation)
   */
  async batchSyncAll(
    userId: string,
    profile: UserProfile,
    quests: Quest[],
    statistic?: DailyActivity,
    bypassRateLimit: boolean = false
  ): Promise<void> {
    try {
      // Use rateLimitedFirestoreService for all operations
      // These operations will be rate-limited unless bypass is enabled
      
      // Profile
      await rateLimitedFirestoreService.saveUserProfile(userId, profile);

      // Quests (limit to 500 per batch - Firestore limit)
      const questsToSync = quests.slice(0, 500);
      if (questsToSync.length > 0) {
        await rateLimitedFirestoreService.saveQuests(userId, questsToSync, bypassRateLimit);
      }

      // Statistic
      if (statistic) {
        await rateLimitedFirestoreService.saveDailyStatistic(userId, statistic);
      }
    } catch (error) {
      console.error('❌ Error batch syncing to Firestore:', error);
      throw error;
    }
  }

  /**
   * Clear any pending syncs
   */
  clearQueue() {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    this.syncQueue.clear();
  }
}

export const firestoreSyncService = new FirestoreSyncService();

